import useElement from "backend/hooks/elements/useElement";
import useFlatHierarchy from "backend/hooks/elements/useFlatHierarchy";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useTopElements from "backend/hooks/model/useTopElements";
import { ICardElementWithChildren } from "backend/model/model";
import sortHierarchy from "backend/utils/sortHierarchy";
import PeriodSelector from "components/PeriodSelector";
import ConfigurableReport from "components/reports/ConfigurableReport";
import ReportsToolbar from "components/toolbars/ReportsToolbar";
import LaunchReport from "design/components/buttons/LaunchReport";
import CollapseCard from "design/components/cards/CollapseCard";
import ConfigReportCard from "design/components/cards/ConfigReportCard";
import CardCheckedBox from "design/components/checkboxes/CardCheckedBox";
import ReportListItem from "design/components/chips/ReportListItem";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import ExportFileSelectionPopUp from "design/components/popups/ExportFileSelectionPopup";
import CircularProgress from "design/components/progress/CircularProgress";
import SideBar from "design/components/toolbar/SideBar";
import ElementTreeView from "design/components/trees/ElementTreeView";
import RowView from "design/layouts/RowView";
import StackView from "design/layouts/StackView";
import TreeViewLeftPanel from "design/layouts/TreeViewLeftPanel";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import React, { FunctionComponent } from "react";
import { buildTree } from "utils/utils";

interface ConfigurableReportsProps {}

const ConfigurableReports: FunctionComponent<ConfigurableReportsProps> = () => {
  const ctx = useAppContext();
  const { data: topElems, isLoading: topElementsLoading } = useTopElements();
  const link = useLink();
  const { data: rootItem } = useElement(ctx.elementId);

  const divRef = React.useRef<HTMLDivElement>(null);
  const [fileType, setFileType] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [fileSelectAnchor, setFileSelectAnchor] =
    React.useState<HTMLDivElement | null>(null);

  const [selectedItems, setSelectedItems] = React.useState<
    ICardElementWithChildren[]
  >([]);
  const selectedKPIs = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "KPI"),
    [selectedItems]
  );
  const selectedInitiatives = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "Initiative"),
    [selectedItems]
  );

  const { data: topElement } = useElement(rootItem?.topelement);

  const { data: subElements, isLoading: isSubElementsLoading } =
    useFlatHierarchy(rootItem?.topelement);

  const listItems = React.useMemo(() => {
    return subElements
      ?.filter((el) => el.typename === "KPI" || el.typename === "Initiative")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subElements]);

  const toggleItem = (item: ICardElementWithChildren) => {
    setSelectedItems((items) => {
      if (items.find((it) => it.id === item.id)) {
        return items.filter((i) => i.id !== item.id);
      }
      return [...items, item];
    });
  };

  return (
    <Page isLoading={topElementsLoading} Toolbar={ReportsToolbar}>
      <StackView gap="0px">
        <div></div>
        <RowView>
          <TreeViewLeftPanel>
            <ElementTreeView
              selectedItem={rootItem}
              items={buildTree(topElems)}
              onSelected={(item) => {
                const userHasAccess = item.accesslevel !== "-1";
                link(userHasAccess ? { meaId: item.scorecardid }:{});
              }}
            />
          </TreeViewLeftPanel>
          <WhiteBgArea>
            <h2>{getTranslatedAttribute(topElement, "Name", ctx.language)}</h2>
            <MeasureGridArea minWidth="calc(100vw - 600px )">
              {listItems ? (
                listItems.map((el) => (
                  <ConfigReportCard
                    item={el}
                    onSelect={() => {
                      toggleItem(el);
                    }}
                  >
                    <CardCheckedBox
                      type="blue"
                      dy={-20}
                      dx={0}
                      isOpen={!!selectedItems.find((kpi) => kpi.id === el.id)}
                    />
                  </ConfigReportCard>
                ))
              ) : isSubElementsLoading ? (
                <CircularProgress />
              ) : null}
            </MeasureGridArea>
          </WhiteBgArea>
          <div>
            <SideBar>
              {selectedItems.length > 0 ? (
                <CollapseCard title={t("reports.Options")} isOpen>
                  <div>{t("reports.Select Period for the Report")}</div>
                  <PeriodSelector />
                  <LaunchReport
                    title={t("reports.Launch Report")}
                    onClick={() => {
                      setFileSelectAnchor(divRef.current);
                    }}
                  />{" "}
                </CollapseCard>
              ) : null}
              <div ref={divRef} style={{ transform: "translateY(-30px)" }} />

              <CollapseCard
                title={`${t("reports.KPIs")} (${selectedKPIs.length})`}
                isOpen={selectedKPIs.length > 0}
              >
                {selectedKPIs.map((it) => (
                  <ReportListItem
                    item={it}
                    itemName={getTranslatedAttribute(it, "Name", ctx.language)}
                    onRemove={(item) => {
                      toggleItem(item);
                    }}
                  />
                ))}
              </CollapseCard>
              <CollapseCard
                isOpen={selectedInitiatives.length > 0}
                title={`${t("reports.Initiatives")} (${
                  selectedInitiatives.length
                })`}
              >
                {selectedInitiatives.map((it) => (
                  <ReportListItem
                    item={it}
                    itemName={getTranslatedAttribute(it, "Name", ctx.language)}
                    onRemove={(item) => {
                      toggleItem(item);
                    }}
                  />
                ))}
              </CollapseCard>

              {fileSelectAnchor ? (
                <>
                  <ExportFileSelectionPopUp
                    onClose={() => {
                      setFileSelectAnchor(null);
                    }}
                    handleClick={(value) => {
                      setFileType(value);
                      setFileSelectAnchor(null);
                      setIsExporting(true);
                    }}
                    anchorEl={fileSelectAnchor}
                  />
                </>
              ) : null}
            </SideBar>
            {isExporting && fileType ? (
              <ConfigurableReport
                items={selectedItems}
                filetype={fileType}
                onClose={() => {
                  setIsExporting(false);
                }}
              />
            ) : null}
          </div>
        </RowView>
      </StackView>
    </Page>
  );
};

export default ConfigurableReports;
